import {
  PageSection,
  PageWrapper,
  PriceRow,
  PromoCodeInput,
} from '@src/components';
import { Package } from '@src/services/packages/packageApiSliceTypes';
import { getCurrencySymbol } from '@src/utilities/currency';
import { PackageCard } from '../Package';
import { useEffect, useState } from 'react';
import { useCheckResponse } from '@src/hooks';
import { useCheckoutMutation } from '@src/services/checkout/checkoutApiSlice';
import { CheckoutResponse } from '@src/services/checkout/checkoutApiSliceTypes';
import { useCreateOrderMutation } from '@src/services/order/orderApiSlice';
import { useLazyGetPackageQuery } from '@src/services/packages/packageApiSlice';
import { FetchQueryError } from '@src/services/api/api.types';
import { Button, Divider, message } from 'antd';
import { ResponsiveModal } from '@src/components/ResponsiveModal';

interface CheckoutProps {
  country: { id: string; flag: string };
  packageId?: string;
  selectedPackage?: Package;
  isOpen: boolean;
  onClose: () => void;
}

export function Checkout({
  country,
  packageId,
  selectedPackage: initialPackage,
  isOpen,
  onClose,
}: Readonly<CheckoutProps>) {
  const { checkResponse } = useCheckResponse();
  const [selectedPackage, setSelectedPackage] = useState<Package | undefined>(
    initialPackage
  );
  const [promoCode, setPromoCode] = useState<string>();
  const [checkoutData, setCheckoutData] = useState<CheckoutResponse>();

  const [checkout, { isLoading: isCheckoutLoading }] = useCheckoutMutation();
  const [createOrder, { isLoading: isOrderLoading }] = useCreateOrderMutation();
  const [getPackage, { isLoading: isLoadingPackage }] =
    useLazyGetPackageQuery();

  useEffect(() => {
    if (!initialPackage && packageId) {
      getPackage({ id: packageId }).then((response) => {
        if ('data' in response) setSelectedPackage(response.data);
      });
    }
  }, [packageId]);

  const fetchCheckout = (code?: string) => {
    if (!selectedPackage) return;

    checkout({
      packageId: selectedPackage.id,
      promoCode: code,
    })
      .then((response) => {
        if (response.error) {
          const err = response.error as FetchQueryError;

          const errorMessage =
            err.data?.message || err.error || 'An unexpected error occurred';
          message.open({
            type: 'error',
            content: errorMessage,
            className: 'response-error',
          });
          setPromoCode(undefined);
        } else {
          setCheckoutData(response.data);
          if (code) {
            setPromoCode(code);
          }
        }
      })
      .catch((error) => {
        checkResponse(error);
        setPromoCode(undefined);
      });
  };

  useEffect(() => {
    if (selectedPackage?.id) {
      fetchCheckout();
    }
  }, [selectedPackage?.id]);

  const handleApplyPromo = (code: string) => {
    fetchCheckout(code);
  };

  const handleOrder = () => {
    if (!selectedPackage) return;

    createOrder({
      countryId: country.id,
      packageId: selectedPackage.id,
      promoCode,
    })
      .then((response) => {
        window.open(response.data?.paymentUrl, '_blank');
      })
      .catch((error) => {
        setPromoCode(undefined);
        checkResponse({ error });
      });
  };

  return (
    <ResponsiveModal
      title='Checkout'
      headerIcon={false}
      isOpen={isOpen}
      onClose={() => onClose()}
      content={
        <PageWrapper loading={isLoadingPackage}>
          {selectedPackage && (
            <PackageCard
              item={selectedPackage}
              country={{ flag: country.flag }}
              showPrice={false}
              className='bg-wl-background'
            />
          )}
          <div className='font-bold text-base text-wl-on-background mt-10 mb-4'>
            Plan details:
          </div>

          <PageSection className='bg-wl-background text-wl-on-background space-y-4 p-4 mb-4'>
            <div className='font-bold'>Order summary</div>
            <PriceRow
              label='eSIM price'
              price={selectedPackage?.price}
              currency={selectedPackage?.currency}
              priceClassName='text-M-20'
            />
            <PriceRow
              label='Discount'
              price={-Number(checkoutData?.discountAmount)}
              currency={selectedPackage?.currency}
              priceClassName='text-M-20 text-discount'
            />

            <Divider className='m-0 !border-wl-on-background opacity-20' />
            <PriceRow
              className='text-R-16'
              label='Total'
              price={checkoutData?.finalAmount}
              currency={selectedPackage?.currency}
              priceClassName='text-B-20'
            />
          </PageSection>
          <PromoCodeInput
            isLoading={isCheckoutLoading}
            discountPercentage={checkoutData?.discountPercentage}
            onApply={handleApplyPromo}
            onRemove={() => {
              setPromoCode(undefined);
              fetchCheckout();
            }}
          />
        </PageWrapper>
      }
      footer={
        <>
          <PageSection className='flex items-center w-full bg-wl-background mt-4'>
            <span className='w-[30%] text-md truncate text-center text-wl-on-background'>
              {checkoutData && checkoutData?.discountAmount > 0 ? (
                <>
                  <div className='text-sm text-wl-secondary line-through'>
                    {getCurrencySymbol(selectedPackage?.currency)}
                    {checkoutData.amount}
                  </div>
                  <div className='text-lg font-semibold'>
                    {getCurrencySymbol(selectedPackage?.currency)}
                    {checkoutData.finalAmount}
                  </div>
                </>
              ) : (
                <div className='text-M-20'>
                  {getCurrencySymbol(selectedPackage?.currency)}
                  {selectedPackage?.price}
                </div>
              )}
            </span>
            <Button
              className='w-[70%] btn-primary'
              loading={isOrderLoading}
              onClick={handleOrder}
            >
              Proceed to Payment
            </Button>
          </PageSection>
          <div className='mt-4 text-R-12 text-center text-wl-on-background'>
            Prices shown exclude VAT. Applicable taxes will be added at checkout
          </div>
        </>
      }
    />
  );
}
